import React from "react";
import { commitMutation, graphql } from "react-relay";

import AppContext from "~/common/AppContext";
import useOnChange from "~/lib/hooks/useOnChange";
import useToggle from "~/lib/hooks/useToggle";
import { getFirstUserFacingError } from "~/lib/utils";
import { contactSupport } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import FellowCheckbox from "~/ui/legacy/Checkbox";
import { HelpCenterIcon, Input, Modal } from "~/ui/legacyFellowIndex";
import toast, { ToastActionButton } from "~/ui/toast";

import type { UserDeletionConfirmModalDeleteInstanceMutation } from "~/__generated__/UserDeletionConfirmModalDeleteInstanceMutation.graphql";

import styles from "./css/settings.module.css";

const UserDeleteInstanceMutation = graphql`
	mutation UserDeletionConfirmModalDeleteInstanceMutation($reason: String!) {
		scheduleUserDeletion(reason: $reason) {
			error {
				message
			}
		}
	}
`;

type UserDeletionConfirmModalProps = {
	canDeleteWorkspace: boolean;
	onClose: () => void;
};

export function UserDeletionConfirmModal({ canDeleteWorkspace, onClose }: UserDeletionConfirmModalProps) {
	const [dataDeleteAgree, toggleDeleteAgree] = useToggle(false);
	const [workspaceDeleteAgree, toggleWorkspaceDeleteAgree] = useToggle(false);

	const [reason, setReason] = React.useState("");
	const { user } = React.useContext(AppContext);
	const onReasonChange = useOnChange<HTMLInputElement>(setReason);
	const [agreeText, setAgreeText] = React.useState("");
	const onAgreeTextChange = useOnChange<HTMLInputElement>(setAgreeText);
	const onContactSupport = React.useCallback(() => {
		contactSupport(`Hi I'd like to delete my account (${user?.email})`);
	}, [user]);

	const onConfirm = React.useCallback(() => {
		commitMutation<UserDeletionConfirmModalDeleteInstanceMutation>(environment, {
			mutation: UserDeleteInstanceMutation,
			variables: { reason },
			onCompleted: (data, errors) => {
				let userFacingErrorMessage = null;

				if (errors && errors.length) {
					userFacingErrorMessage = getFirstUserFacingError(errors);
				}

				const errorMessage = data.scheduleUserDeletion?.error?.message ?? userFacingErrorMessage ?? errors?.[0].message;

				if (errorMessage) {
					toast.error(errorMessage, {
						actions: <ToastActionButton onClick={onContactSupport}>Contact support</ToastActionButton>,
					});
				} else {
					toast.success("Your user account will be permanently deleted in 72 hours.", {
						title: "User account deletion scheduled",
					});
					setTimeout(() => {
						onClose();
						window.location.href = "/";
					}, 2000);
				}
			},
		});
	}, [onClose, reason, onContactSupport]);
	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose}>
				<Modal.Title>Confirm user account deletion</Modal.Title>
				<HelpCenterIcon articleId="6476862" className={styles.helpLink} title="Deleting your account" />
			</Modal.Header>
			<Modal.Body dark>
				<>
					<p>Continue below to proceed with the deletion of your account.</p>
					{canDeleteWorkspace ? (
						<p>
							<b>This operation is irreversible</b> and will result in a complete deletion of all the data associated
							with your user account and all data associated to this Workspace.
						</p>
					) : (
						<p>
							<b>This operation is irreversible</b> and will result in a complete deletion of all the data associated
							with your personal account.
						</p>
					)}
					<form className={styles.userDeletionForm}>
						<label>Reason for deletion:</label>
						<Input
							value={reason}
							placeholder="Please enter a reason for this deletion"
							className={styles.textInput}
							onChange={onReasonChange}
							name="deletion_reason"
						/>

						<label>Enter the text &ldquo;delete&rdquo; to continue:</label>
						<Input
							value={agreeText}
							placeholder="Please enter “delete”"
							className={styles.textInput}
							onChange={onAgreeTextChange}
							name="deletion_confirmation_text"
						/>

						<div className={styles.deleteCheckboxInput}>
							<FellowCheckbox
								green
								checked={dataDeleteAgree}
								onChange={toggleDeleteAgree}
								className={styles.deleteCheckbox}
							/>
							<div className={styles.checkboxConfirmLabel}>
								I acknowledge and understand that all of my personal data will be deleted and want to proceed.
							</div>
						</div>
						{canDeleteWorkspace && (
							<div className={styles.deleteCheckboxInput}>
								<FellowCheckbox
									green
									checked={workspaceDeleteAgree}
									onChange={toggleWorkspaceDeleteAgree}
									className={styles.deleteCheckbox}
								/>
								<div className={styles.checkboxConfirmLabel}>
									I acknowledge that all data associated with my workspace will be deleted and want to proceed.
								</div>
							</div>
						)}
					</form>
				</>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="primary" onClick={onClose}>
					Cancel
				</Button>
				<Button
					size="normal"
					variant="destructive"
					disabled={agreeText !== "delete" || !dataDeleteAgree || (canDeleteWorkspace && !workspaceDeleteAgree)}
					onClick={onConfirm}
				>
					Delete account
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
